import { Popper, styled } from '@mui/material';
import * as React from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

import { VirtualizeProps } from './types';

// From https://mui.com/material-ui/react-autocomplete/#virtualization, MUI 5.13.5.

const LISTBOX_PADDING = 8; // px
const ITEM_HEIGHT = 48; // px
const GROUP_LABEL_HEIGHT = 24; // px
const MAX_ITEMS_COUNT = 8;

const OuterElementContext = React.createContext<React.HTMLAttributes<HTMLElement>>({});

const OuterElementType = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>((props, ref) => {
    const { onScroll: muiOnScroll, ...outerProps } = React.useContext(OuterElementContext);
    // Both react-window and MUI put an `onScroll` on the listbox: react-window's drives the
    // virtual window, MUI's tracks scroll position. Spreading one after the other would drop
    // it — so compose them. (react-window's must run or the window freezes on its first rows.)
    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        props.onScroll?.(event);
        muiOnScroll?.(event);
    };
    return <div ref={ref} {...props} {...outerProps} onScroll={handleScroll} />;
});

function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;
    const item = data.items[index];
    const isLabel = data.groupLabelIndices.has(index);

    if (isLabel) {
        return (
            <div
                style={{
                    ...style,
                    top: (style.top as number) + LISTBOX_PADDING,
                    display: 'flex',
                    alignItems: 'center',
                    boxSizing: 'border-box',
                }}
            >
                {item}
            </div>
        );
    }

    return React.cloneElement(item, {
        style: {
            ...style,
            top: (style.top as number) + LISTBOX_PADDING,
        },
    });
}

const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
    function ListboxComponent(props, ref) {
        // className/style/ref are forwarded via react-window's own props (which merge them onto
        // the scroll element); the remaining listbox attributes (role, aria-*, id, handlers) go
        // through context to be spread on that same element.
        const { children, className, style, ...otherListboxProps } = props;
        const items: React.ReactChild[] = [];
        const groupLabelIndices = new Set<number>();

        React.Children.forEach(
            children as React.ReactChild[],
            (item: React.ReactChild & { props?: VirualizedGroupContainerProps | unknown }) => {
                if (
                    typeof item === 'object' &&
                    item !== null &&
                    'props' in item &&
                    item.props &&
                    typeof item.props === 'object' &&
                    'label' in item.props &&
                    'innerChildren' in item.props
                ) {
                    groupLabelIndices.add(items.length);
                    items.push(item.props.label as React.ReactChild);
                    items.push(...((item.props.innerChildren as React.ReactChild[]) || []));
                } else {
                    items.push(item);
                }
            },
        );

        const itemCount = items.length;
        const getItemSize = (index: number) => (groupLabelIndices.has(index) ? GROUP_LABEL_HEIGHT : ITEM_HEIGHT);
        const totalHeight = Array.from({ length: itemCount }, (_, i) => getItemSize(i)).reduce((a, b) => a + b, 0);
        const maxHeight = MAX_ITEMS_COUNT * ITEM_HEIGHT;
        const innerHeight = Math.min(totalHeight, maxHeight);

        const listRef = React.useRef<VariableSizeList>(null);
        React.useEffect(() => {
            listRef.current?.resetAfterIndex(0);
        }, [itemCount]);

        const itemData = { items, groupLabelIndices };

        return (
            <OuterElementContext.Provider value={otherListboxProps}>
                <VariableSizeList
                    ref={listRef}
                    className={className}
                    style={style}
                    outerRef={ref}
                    itemData={itemData}
                    height={innerHeight + 2 * LISTBOX_PADDING}
                    width="100%"
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={getItemSize}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        );
    },
);

const StyledPopper = styled(Popper)({
    [`& .MuiAutocomplete-listbox`]: {
        boxSizing: 'border-box',
        '& ul': {
            padding: 0,
            margin: 0,
        },
    },
});

export function generateVirtualizeProps(): VirtualizeProps {
    return {
        listbox: ListboxComponent,
        popper: StyledPopper,
    };
}

type VirualizedGroupContainerProps = {
    label: React.ReactNode;
    innerChildren: React.ReactNode;
};

/** Only for `renderGroup` of a virtualized FieldMultiSelect: the listbox reads its props to flatten the groups. */
export const VirualizedGroupContainer: React.FunctionComponent<VirualizedGroupContainerProps> = (): null => {
    // HACK: renders nothing; the listbox takes its props to count the rows without walking nested children.
    return null;
};
